import React, { ChangeEvent, Dispatch, MutableRefObject, useEffect, useRef, useState } from 'react'
import { APIGetAllRoles, APIGetAllUsers, ResponseUserRole, UserData } from './IncidentManageService'
import LoadingDark from '../../images/icons/load-dark.gif'
import LoadingLight from '../../images/icons/load-light.gif'
import { useTheme } from '../../context/ThemeContext'

type RoleTablePropsType = {
    data: ResponseUserRole[]
    setData: Dispatch<React.SetStateAction<ResponseUserRole[]>>
    allData: Dispatch<React.SetStateAction<ResponseUserRole[]>>
}

const RoleTable = (props: RoleTablePropsType) => {
    const { theme } = useTheme()
    
    const areaSpecificity = new Map<string, string>([
        ['area_specific', 'Area Specific'],
        ['whole_country', 'Whole Country']
    ])

    const [selectedCodes, setSelectedCodes] = useState(new Set());
    const rowNumber = useRef<HTMLInputElement | null>(null)
    const searchText = useRef<HTMLInputElement | null>(null)

    const [selectedPage, setSelectedPage] = useState<number>(0)
    const [sliceCount, setSliceCount] = useState<number>(10)

    const [userData, setUserData] = useState<ResponseUserRole[]>([])
    const [filteredData, setFilteredData] = useState<ResponseUserRole[]>([])
    const [slicedData, setSlicedData] = useState<ResponseUserRole[][]>([])

    const [isTableDataLoading, setIsTableDataLoading] = useState<boolean>(false)
    

    const paginationList = (data: ResponseUserRole[], size: number): ResponseUserRole[][] => {
        const result: ResponseUserRole[][] = []
    
        if (size >= data.length) {
            return [data]
        }
    
        const length = data.length
    
        let start = 0
    
        let isOk = true
        while(isOk) {
            if (start < length) {
                result.push(data.slice(start, start + size))
                start += size
            } else {
                result.push(data.slice(start, -1))
                isOk = false
            }
        }
        return result
    }

    const paginationNavigation = (side: "next" | "prev") => {
        if (side === "next") {
            if (slicedData.length > selectedPage + 1) {
                setSelectedPage(prevState => prevState + 1)
            }
           
        } else if (side === "prev") {
            if (0 <= selectedPage - 1){
                setSelectedPage(prevState => prevState - 1)
            }
        }
    }

    // Handle slice count changed
    const handleSliceCountChange = (event: ChangeEvent<HTMLInputElement>) => {
        setSliceCount(event.target.valueAsNumber)
    }

    // handle search text change
    const handleSearchTextChange = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value
        if (value === '') {
            setFilteredData(userData)
        } else {
            setFilteredData(prevState => userData.filter(item => item.rolename.toLowerCase().includes(value.toLowerCase()) || item.roledescription.toLowerCase().includes(value.toLowerCase())))
        }
    }

    useEffect(() => {
        if (rowNumber.current){
            setSlicedData(prevState => paginationList(filteredData, sliceCount))
        }
    }, [sliceCount, filteredData])

    useEffect(() => {
        setIsTableDataLoading(true)
        const getRoleData = async () => {
            const data = await APIGetAllRoles()

            if (data.status)
            {
                setUserData(data.data)
                setFilteredData(data.data)
                props.allData(data.data)
            }
            else
            {
                
            }
            setIsTableDataLoading(false)
        }
        getRoleData()
    }, [])

    const handleCheckRole = (data: ResponseUserRole) => {
        const isExist = props.data.find(item => item.roleid === data.roleid)

        if(isExist)
        {
            props.setData(prevState => prevState.filter(item => item.roleid !== data.roleid))
        }
        else 
        {
            props.setData(prevState => [...prevState, data])
        }
    }

    return (
        <div className='subTable-wrapper'>
            <span className='main-title'>Select Roles</span>
            <div className='field-wrapper'>
                <div className='left'>
                    <span>show <input type="number" ref={rowNumber} value={sliceCount} min={1} onChange={(e) => handleSliceCountChange(e)} /> entries</span>
                </div>
                <div className='right'>
                    <span> search <input type="text" ref={searchText} onChange={(e) => handleSearchTextChange(e)} placeholder='Search here' /></span>
                </div>
            </div>
            <div className='table-body'>
                <div className='table-wrapper'>
                    <div className='table-row header' style={{ gridTemplateColumns: `3rem 1fr 1fr 1fr`, width: '100%' }}>
                        <span></span>
                        <span>Role</span>
                        <span>Description</span>
                        <span>Area Specificity</span>
                    </div>
                </div>
                {slicedData[selectedPage] && slicedData[selectedPage].map((row, index) => (
                    <div key={index} className='table-wrapper'>
                        <div className='table-row' style={{ gridTemplateColumns: `3rem 1fr 1fr 1fr`, width: '100%' }}>
                            <span style={{ justifyContent: 'center' }}>
                                <input
                                    type="checkbox"
                                    name={row.roleid}
                                    onChange={(e) => handleCheckRole(row)}
                                    checked={props.data.some(item => item.roleid === row.roleid)}
                                />
                            </span>
                            <span>{row.rolename}</span>
                            <span>{row.roledescription}</span>
                            <span>{areaSpecificity.get(row.roleAreaSpecificity)}</span>
                        </div>
                    </div>
                ))}
            </div>
            
            {filteredData.length === 0 && !isTableDataLoading && (
                <div className='table-no-data-wrapper'>
                    <span>no any data available</span>
                </div>
            )}

            {isTableDataLoading && (
                <div className='loading-wrapper' style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
                    <img style={{ width: '3rem' }} src={theme === 'dark' ? LoadingDark : LoadingLight} alt="" />
                    <span style={{ color: 'var(--color-text)' }}>User roles are loading...</span>
                </div>
            )}

            <div className='table-pagination-wrapper'>
                <div className='icon-page-box' style={{ marginBottom: "0.5rem" }} onClick={(e) => paginationNavigation("prev")}>&lt;</div>
                <div className='page-wrapper'>
                    {slicedData.map((page, index) => (
                        <span key={index} className={selectedPage === index ? 'icon-page-box selected' : 'icon-page-box'} onClick={(e) => setSelectedPage(index)}>{index + 1}</span>
                    ))}
                </div>
                <div className='icon-page-box' style={{ marginBottom: "0.5rem" }} onClick={(e) => paginationNavigation("next")}>&gt;</div>
            </div>
        </div>
    )
}

export default RoleTable